import React from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Users, Zap, Star } from "lucide-react";

/**
 * PremiumToolShell — reusable Apple-inspired wrapper for any tool page.
 *
 * Aesthetic rules (strict):
 * - 4-color palette only: #A855F7 (brand), #111111 (ink), #FFFFFF (canvas), slate ramp
 * - No multi-colour gradients
 * - 20–24px corner radius for cards
 * - Outfit/Inter typography (inherited from body)
 * - Generous whitespace and centered max-width layout
 *
 * Props:
 *  - icon: ReactNode (an inline svg/lucide icon)
 *  - eyebrow: small "FREE TOOL" / "AI" label above title
 *  - title: main H1
 *  - description: subtitle paragraph
 *  - socialProof: { users?, searches?, rating? }  (optional)
 *  - children: the main interactive card content
 *  - footerCTA: optional ReactNode rendered below the main card (e.g. upgrade banner)
 *  - sideContent: optional ReactNode rendered after main card (e.g. RelatedTools, SEO content)
 */
export default function PremiumToolShell({
  icon,
  eyebrow = "FREE TOOL",
  title,
  description,
  socialProof,
  children,
  footerCTA,
  sideContent,
}) {
  return (
    <div className="min-h-screen bg-white" data-testid="premium-tool-shell">
      <Header />

      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero */}
        <header className="text-center mb-12">
          {icon && (
            <div
              className="w-14 h-14 mx-auto mb-6 rounded-2xl bg-[#111111] flex items-center justify-center text-white shadow-sm"
              data-testid="premium-tool-icon"
            >
              {icon}
            </div>
          )}

          {eyebrow && (
            <p
              className="text-xs tracking-[0.18em] font-semibold text-[#A855F7] mb-3 uppercase"
              data-testid="premium-tool-eyebrow"
            >
              {eyebrow}
            </p>
          )}

          <h1
            className="text-4xl sm:text-5xl font-semibold tracking-tight text-[#111111] mb-4"
            style={{ fontFamily: "'Outfit', 'Inter', system-ui, sans-serif" }}
            data-testid="premium-tool-title"
          >
            {title}
          </h1>

          {description && (
            <p
              className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed"
              data-testid="premium-tool-description"
            >
              {description}
            </p>
          )}

          {socialProof && (
            <div
              className="flex items-center justify-center gap-8 mt-8 text-sm text-slate-500"
              data-testid="premium-tool-social-proof"
            >
              {socialProof.users && (
                <div className="flex items-center gap-1.5">
                  <Users className="w-4 h-4 text-[#A855F7]" />
                  <span className="font-semibold text-[#111111]">
                    {socialProof.users}
                  </span>
                  <span>users</span>
                </div>
              )}
              {socialProof.searches && (
                <div className="flex items-center gap-1.5">
                  <Zap className="w-4 h-4 text-[#A855F7]" />
                  <span className="font-semibold text-[#111111]">
                    {socialProof.searches}
                  </span>
                  <span>searches</span>
                </div>
              )}
              {socialProof.rating && (
                <div className="flex items-center gap-1.5">
                  <Star className="w-4 h-4 text-[#A855F7] fill-[#A855F7]" />
                  <span className="font-semibold text-[#111111]">
                    {socialProof.rating}
                  </span>
                  <span>rating</span>
                </div>
              )}
            </div>
          )}
        </header>

        {/* Main interactive card */}
        <Card
          className="p-6 md:p-10 rounded-3xl border border-slate-200 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.04)] bg-white"
          data-testid="premium-tool-card"
        >
          {children}
        </Card>

        {footerCTA && <div className="mt-12">{footerCTA}</div>}
        {sideContent && <div className="mt-12">{sideContent}</div>}
      </main>

      <Footer />
    </div>
  );
}

/**
 * PremiumStat — clean square stat tile (replaces colored bg-blue-50/bg-pink-50 etc).
 */
export function PremiumStat({ value, label, accent = false }) {
  return (
    <div
      className={`p-4 rounded-2xl text-center border ${
        accent
          ? "border-[#A855F7]/20 bg-[#FAF5FF]"
          : "border-slate-200 bg-white"
      }`}
    >
      <p
        className={`text-3xl font-semibold ${
          accent ? "text-[#A855F7]" : "text-[#111111]"
        }`}
      >
        {value}
      </p>
      <p className="text-xs text-slate-500 mt-1">{label}</p>
    </div>
  );
}

/**
 * PremiumUpgradeBanner — flat black CTA banner replacing pink/purple gradient promos.
 */
export function PremiumUpgradeBanner({
  title = "Want unlimited access?",
  subtitle = "Unlock 75+ AI tools, AI assistants, and team workspaces.",
  ctaLabel = "Start free trial",
  ctaHref = "/pricing",
}) {
  return (
    <Card
      className="p-10 rounded-3xl bg-[#111111] text-white text-center border-0"
      data-testid="premium-upgrade-banner"
    >
      <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-2">
        {title}
      </h2>
      <p className="text-slate-300 mb-6 max-w-xl mx-auto">{subtitle}</p>
      <a
        href={ctaHref}
        className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[#111111] font-medium hover:bg-slate-100 transition"
        data-testid="premium-upgrade-cta"
      >
        {ctaLabel}
        <span aria-hidden>→</span>
      </a>
    </Card>
  );
}

/**
 * PremiumFeatureRow — 3-up minimal feature list (replaces colored icon circles).
 */
export function PremiumFeatureRow({ items }) {
  return (
    <div className="grid md:grid-cols-3 gap-8 my-12">
      {items.map((f, i) => (
        <div key={i} className="text-center">
          <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-slate-100 flex items-center justify-center text-[#111111]">
            {f.icon}
          </div>
          <h3 className="font-semibold text-[#111111] mb-1">{f.title}</h3>
          <p className="text-sm text-slate-500">{f.desc}</p>
        </div>
      ))}
    </div>
  );
}
